import { useEffect, useRef, useState } from 'react';
import { Ban, Star } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { apiClient } from '@/lib/apiClient';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { MARTINA_RATING_BUTTON_CLASS } from '@/components/exai/martinaDesignTokens';
import { martinaUiErrorMessage } from '@/components/exai/martinaUiError';
import { useMartinaRoomPriority } from '@/hooks/useMartinaRoomPriority';
import { isMartinaRoomVoteOutput, martinaAttentionScoreToStars, martinaStarsToAttentionScore } from '@shared/martinaRoomRating';

type RatingResponse = {
  signalDisposition?: { roomId: number; state: string } | null;
};

export const MARTINA_ATTENTION_SIGNAL_DISPOSITION_EVENT = 'martina:attention-signal-disposition';

/** Riutilizzabile su qualunque output Martina: non conosce l'entita', registra
 * soltanto il riferimento canonico e il contesto minimo per l'audit. */
export function MartinaOutputRating({
  roomId,
  outputType,
  outputId,
  context = {},
  label = 'Quanto ti e utile?',
  compact = false,
  className,
  onSignalDisposition,
}: {
  roomId?: number | null;
  outputType: string;
  outputId: string | number;
  context?: Record<string, unknown>;
  /** Il nome della cosa valutata cambia per Stanza, email, task e meeting. */
  label?: string;
  /** Variante senza riquadro interno, per la riga finale delle card. */
  compact?: boolean;
  className?: string;
  /** Lo zero esplicito chiude il Segnale; la Stanza e le fonti restano invariate. */
  onSignalDisposition?: (disposition: { roomId: number; state: string }) => void;
}) {
  const { toast } = useToast();
  const isRoomVote = roomId != null && isMartinaRoomVoteOutput(outputType);
  const roomVote = useMartinaRoomPriority(isRoomVote ? roomId! : null, outputType, outputId);
  const [genericSaving, setSaving] = useState(false);
  const [genericLoading, setLoading] = useState(true);
  const [genericSelected, setSelected] = useState<number | null>(null);
  const saving = isRoomVote ? roomVote.saving : genericSaving;
  const loading = isRoomVote ? roomVote.loading : genericLoading;
  const selected = isRoomVote ? roomVote.score == null ? null : martinaAttentionScoreToStars(roomVote.score) : genericSelected;
  // Un GET iniziato all'apertura puo' terminare dopo il POST del click e
  // riportare graficamente il valore precedente. La revisione rende il
  // readback subordinato alle scritture effettuate nel frattempo.
  const writeRevision = useRef(0);

  useEffect(() => {
    if (isRoomVote) return;
    let active = true;
    const revisionAtStart = writeRevision.current;
    setLoading(true);
    setSelected(null);
    const params = new URLSearchParams({
      outputType,
      outputId: String(outputId),
    });
    void apiClient(`/api/exai/ratings?${params.toString()}`)
      .then((response: { rating?: number } | null) => {
        if (!active || writeRevision.current !== revisionAtStart) return;
        const rating = Number(response?.rating);
        setSelected(Number.isInteger(rating) && rating >= 0 && rating <= 5 ? rating : null);
      })
      .catch(() => {
        // Il readback non deve trasformare una superficie di feedback in un
        // errore bloccante: il POST continua a essere la fonte di scrittura.
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, [outputId, outputType, isRoomVote]);

  const rate = async (rating: number) => {
    if (saving) return;
    const previous = selected;
    writeRevision.current += 1;
    setSelected(rating);
    setSaving(true);
    try {
      const response = (isRoomVote ? await roomVote.save(martinaStarsToAttentionScore(rating), { ...context, feedbackGesture: 'stars' }) : await apiClient('/api/exai/ratings', {
        method: 'POST',
        body: JSON.stringify({ roomId: roomId ?? null, outputType, outputId, rating, context }),
      })) as RatingResponse | null;
      if (!response) return;
      setSelected(rating);
      const signalDisposition = response.signalDisposition ?? null;
      if (signalDisposition) {
        window.dispatchEvent(new CustomEvent(MARTINA_ATTENTION_SIGNAL_DISPOSITION_EVENT, { detail: signalDisposition }));
        onSignalDisposition?.(signalDisposition);
      }
      toast({
        title: signalDisposition ? 'Segnale archiviato' : 'Valutazione salvata',
        description: signalDisposition
          ? 'Non verrà più proposto in attenzione; la Stanza e le fonti restano nella cronologia.'
          : rating >= 4 ? 'Martina dara più peso a casi simili, dopo evidenze ripetute.' : rating <= 1 ? 'Martina dara meno peso a casi simili, senza nascondere questa situazione.' : 'Martina conserva il giudizio senza cambiare subito le priorita.',
      });
    } catch (error) {
      setSelected(previous);
      toast({ title: 'Valutazione non salvata', description: martinaUiErrorMessage(error, 'Martina non ha registrato la valutazione. Nessun fatto o urgenza è stato modificato.'), variant: 'destructive' });
    } finally { setSaving(false); }
  };

  return (
    <div className={cn(
      'flex flex-row flex-wrap items-center gap-1 text-xs',
      compact ? 'w-full min-w-0 justify-between overflow-hidden border-0 bg-transparent p-0' : 'border border-border bg-background p-2',
      className,
    )}>
      <span className={cn('min-w-0 max-w-full whitespace-normal break-words text-muted-foreground', compact ? 'text-[11px]' : 'text-base')}>{isRoomVote ? 'La tua priorità' : label}</span>
      <div className="flex min-w-0 flex-wrap items-center">
        {(compact && !isRoomVote ? [1, 2, 3, 4, 5] : [0, 1, 2, 3, 4, 5]).map((rating) => (
          <Button
            key={rating}
            type="button"
            variant="ghost"
            size="sm"
            className={cn(
              MARTINA_RATING_BUTTON_CLASS,
              selected === rating && 'bg-primary/15 text-primary ring-1 ring-inset ring-primary/50 hover:bg-primary/20',
            )}
            disabled={saving || loading}
            aria-label={rating === 0 ? 'Valuta 0 su 5: non riproporre salvo nuovi fatti' : `Valuta ${rating} su 5`}
            aria-pressed={selected === rating}
            title={selected === rating ? `Valutazione salvata: ${rating}/5` : undefined}
            onClick={() => void rate(rating)}
          >
            {rating === 0
              ? <Ban className={`h-5 w-5 ${selected === 0 ? 'text-primary' : 'text-muted-foreground'}`} />
              : <Star className={`${compact ? 'h-4 w-4' : 'h-5 w-5'} ${selected !== null && selected > 0 && rating <= selected ? 'fill-primary text-primary' : 'text-muted-foreground'}`} />}
          </Button>
        ))}
      </div>
    </div>
  );
}
